import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { DialogContent, DialogActions } from '@mui/material';
import { useGroups } from '@app/api/groups/useGroups';
import { useMembers } from '@app/api/members/useMembers';
import { fetchAllGroups } from '@app/store/slices/myTeamSlice';
import { roles, getUserRole } from '@shared/static/roles';
import TabsComponent from '@shared/ui/TabsComponent';
import { ThemeButton } from '@shared/ui/buttons';
import { FormGroup } from '@shared/ui/forms/display';
import {
  EmailInput,
  TelegramInput,
  InviteTeamPicker,
  MemberNameInput,
} from '@shared/ui/forms/inputs';
import { useEditMemberDialog } from '@utils/dialogs-hooks';
import { validateServerErrors } from '@utils/validation';

export default function NewMemberDialog({ close, data }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.user);

  const [role, setRole] = useState(roles.TEAM_MEMBER);
  const [groupsWithoutTeamLead, setGroupsWithoutTeamLead] = useState([]);

  // Кеширование данных на время жизни модалки
  const [allGroupsLoaded, setAllGroupsLoaded] = useState(false);
  const [groupsWithoutTeamLeadLoaded, setGroupsWithoutTeamLeadLoaded] =
    useState(false);

  const { inviteMember } = useMembers();
  const { openEditMemberDialog } = useEditMemberDialog();
  const { fetchGroupsWithoutTeamLead } = useGroups();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    setError,
  } = useForm({
    defaultValues: {
      telegram: '',
      group_id: data?.id || null,
    },
  });

  // Определяем доступные роли в зависимости от роли текущего пользователя
  // Если пользователь - тимлид, он может создавать только обычных участников
  const member = {
    label: t(getUserRole(roles.TEAM_MEMBER)),
    value: roles.TEAM_MEMBER,
  };

  const lead = {
    label: t(getUserRole(roles.TEAM_LEAD)),
    value: roles.TEAM_LEAD,
  };

  const memberRoles =
    user?.group_role === roles.TEAM_LEAD ? [member] : [member, lead];

  const onSubmit = async (formData) => {
    try {
      const res = await inviteMember({
        group_role: role,
        ...formData,
      });

      if (res) {
        close();
        openEditMemberDialog({ isNew: true, ...res });
      }
    } catch (error) {
      validateServerErrors(setError, error);
    }
  };

  // Установка group_id при инициализации и переключении ролей
  // TEAM_MEMBER: всегда group.id
  // TEAM_LEAD: group.id только если teamLead === null, иначе null
  useEffect(() => {
    if (!data?.id) {
      setValue('group_id', null);
      return;
    }

    if (role === roles.TEAM_MEMBER) {
      setValue('group_id', data.id);
    } else if (role === roles.TEAM_LEAD) {
      setValue('group_id', !data.teamLead ? data.id : null);
    }
  }, [data?.id, data?.teamLead, role, setValue]);

  // Загрузка данных при открытии селекта (с кешированием)
  // Для TEAM_LEAD: загружаем группы без тимлида
  // Для TEAM_MEMBER: загружаем все группы в Redux
  async function handleSelectOpen() {
    if (role === roles.TEAM_LEAD) {
      if (!groupsWithoutTeamLeadLoaded) {
        const groups = await fetchGroupsWithoutTeamLead();
        if (groups) setGroupsWithoutTeamLead(groups);
        setGroupsWithoutTeamLeadLoaded(true);
      }
    } else if (!allGroupsLoaded) {
      await dispatch(fetchAllGroups());
      setAllGroupsLoaded(true);
    }
  }

  return (
    <>
      <DialogContent>
        <div className="form">
          <FormGroup label={t('role')}>
            <TabsComponent
              fullWidth
              isGroup
              value={role}
              setValue={setRole}
              options={memberRoles}
            />
          </FormGroup>

          <MemberNameInput control={control} errors={errors} />
          <EmailInput control={control} errors={errors} />
          <TelegramInput control={control} errors={errors} />

          <InviteTeamPicker
            control={control}
            errors={errors}
            groupsWithoutTeamLead={groupsWithoutTeamLead}
            role={role}
            group={data}
            onSelectOpen={handleSelectOpen}
            loading={
              role === roles.TEAM_LEAD
                ? !groupsWithoutTeamLeadLoaded
                : !allGroupsLoaded
            }
          />
        </div>
      </DialogContent>

      <DialogActions>
        <ThemeButton
          onClick={handleSubmit(onSubmit)}
          endIcon="chevron-right"
          loading={isSubmitting}
          block={false}
          minWidth
        >
          {t('invite')}
        </ThemeButton>
      </DialogActions>
    </>
  );
}
